import React from 'react';
import { FiZap } from 'react-icons/fi';
import { useLanguage } from '../Context/LanguageContext';
import CustomTooltip from './CustomTooltip';
import './AIBadge.css';

/**
 * AIBadge - Shows a small badge indicating AI-suggested content
 *
 * @param {Object} props
 * @param {number} props.confidence - Confidence score from 0 to 1
 * @param {string} props.size - Size variant: 'sm', 'md', or 'lg' (default: 'sm')
 * @param {boolean} props.showConfidence - Whether to show confidence level text
 * @param {string} props.className - Additional CSS classes
 */
const AIBadge = ({
  confidence = 0.8,
  size = 'sm',
  showConfidence = false,
  className = '',
}) => {
  const { t } = useLanguage();

  // Determine confidence level
  const getConfidenceLevel = (score) => {
    if (score >= 0.85) return { level: 'high', label: t('common.highConfidence') || 'High confidence' };
    if (score >= 0.6) return { level: 'medium', label: t('common.mediumConfidence') || 'Medium confidence' };
    return { level: 'low', label: t('common.lowConfidence') || 'Review suggested' };
  };

  const { level, label } = getConfidenceLevel(confidence);

  return (
    <CustomTooltip
      content={`${t('common.aiSuggested') || 'AI Suggested'} - ${label}`}
      placement="top"
      showOnMobile={true}
    >
      <span className={`ai-badge ai-badge-${size} ai-badge-${level} ${className}`}>
        <FiZap size={size === 'sm' ? 10 : size === 'md' ? 12 : 14} />
        {showConfidence && (
          <span className="ai-badge-text">
            {t('common.aiSuggested') || 'AI'}
          </span>
        )}
      </span>
    </CustomTooltip>
  );
};

/**
 * AIBadgeWrapper - Wraps a form field with an AI badge indicator
 */
export const AIBadgeWrapper = ({ children, showBadge = false, confidence = 0.8 }) => {
  if (!showBadge) return children;

  return (
    <div className="ai-badge-wrapper">
      {children}
      <AIBadge confidence={confidence} size="sm" />
    </div>
  );
};

export default AIBadge;
